import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { schoolRequestsApi, type SchoolRequestDto, type SchoolRequestApprovalDto, type SchoolRequestReassignmentDto } from '../api/school-requests.api';
import { usersApi } from '../../users/api/users.api';
import { getErrorMessage } from '@/core/api/response-handler';
import { mesajEroareCorectare } from '../utils/erori-corectare-formular';

export function useSchoolRequestsMutations() {
    const queryClient = useQueryClient();

    // Invalidate school requests report query after any mutation
    const invalidateReport = () => {
        queryClient.invalidateQueries({ queryKey: ['school-requests', 'report'] });
    };

    // 1. Approve request
    const approveRequest = useMutation({
        mutationFn: (schoolRequestId: string) =>
            schoolRequestsApi.updateApprovalStatus({
                schoolRequestId,
                status: 'APPROVED'
            } as SchoolRequestApprovalDto),
        onSuccess: () => {
            toast.success('Cererea a fost aprobată cu succes');
            invalidateReport();
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la aprobarea cererii'));
        },
    });

    // 2. Reject request
    const rejectRequest = useMutation({
        mutationFn: ({ schoolRequestId, rejectReason }: { schoolRequestId: string; rejectReason: string }) =>
            schoolRequestsApi.updateApprovalStatus({
                schoolRequestId,
                status: 'REJECTED',
                rejectReason
            } as SchoolRequestApprovalDto),
        onSuccess: () => {
            toast.success('Cererea a fost respinsă');
            invalidateReport();
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la respingerea cererii'));
        },
    });

    // 3. Reassign professor
    const reassignProfessor = useMutation({
        mutationFn: ({ schoolRequestId, newProfessorEmail }: { schoolRequestId: string; newProfessorEmail: string }) =>
            schoolRequestsApi.reassignProfessor({
                schoolRequestId,
                newProfessorEmail
            } as SchoolRequestReassignmentDto),
        onSuccess: () => {
            toast.success('Profesorul a fost schimbat cu succes');
            invalidateReport();
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la schimbarea profesorului'));
        },
    });

    // 4. Update form data (admin corrections)
    const updateFormData = useMutation({
        mutationFn: (data: SchoolRequestDto) =>
            schoolRequestsApi.updateByAdmin(data),
        onSuccess: () => {
            toast.success('Formularul a fost actualizat cu succes');
            invalidateReport();
        },
        // Numeste campul respins: titlul serverului singur («Eroare la validarea datelor…») nu
        // spunea nimic adminului (07.10). Erorile pe campurile din dialog le afiseaza pagina.
        onError: (error) => {
            toast.error(mesajEroareCorectare(error));
        },
    });

    // 5. Delete request
    const deleteRequest = useMutation({
        mutationFn: (schoolRequestId: string) =>
            schoolRequestsApi.delete(schoolRequestId),
        onSuccess: () => {
            toast.success('Cererea a fost ștearsă cu succes');
            invalidateReport();
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la ștergerea cererii'));
        },
    });

    // 6. Update phone note
    const updatePhoneNote = useMutation({
        mutationFn: ({ userId, phoneCallNote }: { userId: string; phoneCallNote: string }) =>
            usersApi.updatePhoneNote(userId, phoneCallNote),
        onSuccess: () => {
            toast.success('Notițele au fost salvate cu succes');
            invalidateReport();
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la salvarea notițelor'));
        },
    });

    // Combined loading state for disabling buttons
    const isLoading =
        approveRequest.isPending ||
        rejectRequest.isPending ||
        reassignProfessor.isPending ||
        updateFormData.isPending ||
        deleteRequest.isPending ||
        updatePhoneNote.isPending;

    return {
        approveRequest,
        rejectRequest,
        reassignProfessor,
        updateFormData,
        deleteRequest,
        updatePhoneNote,
        isLoading,
        // Individual loading states for dialog components
        isApproving: approveRequest.isPending,
        isRejecting: rejectRequest.isPending,
        isUpdatingPhoneNote: updatePhoneNote.isPending,
        isReassigningProfessor: reassignProfessor.isPending,
        isUpdatingFormData: updateFormData.isPending,
    };
}
